import type { ChunkSet } from '../../gen/rooms';

/**
 * Hand-made room chunks for Mars (GDD: each planet adds one movement idea; Mars's is the dust
 * vent). A vent (`^`) blows an updraft up to eight tiles tall that lifts the suit for free, no
 * Starlight spent; a vent at the bottom of a pit throws a falling suit back out. Gravity is a
 * little under Earth's (a jump rises about 3.5 tiles). Same format and seam rule as the other
 * chunk sets: `.` empty · `#` solid · `=` one-way · `P` start · `*` Starlight cell ·
 * `m` material shard · `^` dust vent · `~` radiation · `w` weapon mod cache · `c` crawler ·
 * `d` drone · `X` exit.
 */
const CHUNKS = {
  start: {
    id: 'r-start',
    // The landing site: flat ground and a first dust vent to ride, cells at its top.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..............**........',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...P..........^^........',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...............*........',
        '.............=====......',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '................*.......',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  geyser: {
    id: 'geyser',
    // A mesa seven tiles tall: the vent at its foot lifts you onto it; a shard hangs above (jet).
    lanes: [
      [
        '........................',
        '.............m..........',
        '............====........',
        '........................',
        '........................',
        '........................',
        '.............**.........',
        '..........########......',
        '..........########......',
        '..........########......',
        '..........########......',
        '..........########......',
        '..........########......',
        '........^^########......',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '............d...........',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '......c.................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...............*........',
        '.........c..............',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  blowhole: {
    id: 'blowhole',
    // A pit floored with vents: drop into the dust anywhere and it throws you out again.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........**...........',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '######............######',
        '######^^^^^^^^^^^^######',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........d............',
        '........................',
        '........................',
        '........................',
        '........................',
        '#########.....##########',
        '#########.....##########',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.......c........c.......',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  terraces: {
    id: 'terraces',
    // Three terraces, each a jump high; behind them, a vent up to a shard.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '.............**.........',
        '............####........',
        '............####........',
        '............####........',
        '........########........',
        '........########..d.....',
        '........########........',
        '....############........',
        '....############........',
        '....############........',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.....=====..............',
        '........................',
        '........................',
        '..............c.........',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '..............m.........',
        '.............====.......',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..............^^........',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  rover: {
    id: 'rover',
    // An abandoned rover: a girder frame over a crawler; a vent behind it to a shard.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.........**.............',
        '......========..........',
        '.......#....#...........',
        '.......#....#...........',
        '.......#.c..#...........',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '................m.......',
        '...............====.....',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........c.......^^......',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........d............',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  canyon: {
    id: 'canyon',
    // A wide trench in the near lane with vents along its floor: ride across on the dust; a narrower one behind.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.........*.....*........',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '####................####',
        '####.^^.^^.^^.^^.^^.####',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........d............',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '########........########',
        '########..^^^^..########',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '............*...........',
        '......c..........c......',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  dunes: {
    id: 'dunes',
    // Rocks to hop among crawlers, a drone overhead; a mod cache on the plank.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '............d...........',
        '........................',
        '...............*........',
        '........................',
        '..............###.......',
        '......##......###.......',
        '......##..c...###.c.....',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..........*w............',
        '.........====...........',
        '........................',
        '........................',
        '........................',
        '.................c......',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........##...........',
        '.....c.....##...........',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  fallout: {
    id: 'fallout',
    // A mesa up a vent; its far end is a dead probe's fallout (tier 2), round a cache and a shard.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..................*.....',
        '........................',
        '..........##............',
        '..........##...c........',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '...........~~~~~~~~~....',
        '...........~~~~~m~~~....',
        '...........~~~~~~~~~....',
        '.........*.~~~w~~~~~....',
        '........##########~~....',
        '........##########~~....',
        '........##########~~....',
        '........##########~~....',
        '........##########~~....',
        '......^^##########~~....',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '................d.......',
        '........................',
        '........................',
        '........................',
        '........................',
        '........c...............',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  exit: {
    id: 'r-exit',
    // The way out: a door in every lane, against the end wall.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '....................XX##',
        '....................XX##',
        '........*...........XX##',
        '....................XX##',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
} as const;

export const MARS_CHUNKS: ChunkSet = {
  start: CHUNKS.start,
  middle: [
    CHUNKS.geyser,
    CHUNKS.blowhole,
    CHUNKS.terraces,
    CHUNKS.rover,
    CHUNKS.canyon,
    CHUNKS.dunes,
    CHUNKS.fallout,
  ],
  exit: CHUNKS.exit,
};
